/**
 * Copyright (c) 2024-present, Bruno Carvalho de Araujo.
 * All rights reserved.
 *
 * This source code is licensed under the license found in the LICENSE file in
 * the root directory of this source tree.
 */

import 'bootstrap/dist/css/bootstrap.min.css'
import React from 'react'
import 'react-bootstrap-typeahead/css/Typeahead.css'
import ReactDOM from 'react-dom/client'
import 'react-inner-image-zoom/lib/InnerImageZoom/styles.css'
import 'react-quill/dist/quill.snow.css'
import 'react-toastify/dist/ReactToastify.css'
import App from './App'
import reportWebVitals from './reportWebVitals'

// Puck/Quill can trigger a benign ResizeObserver loop in the same frame as layout updates.
// CRA dev overlay treats it as fatal; suppress so save/publish flows are not blocked.
const resizeObserverLoopError = /ResizeObserver loop (completed with undelivered notifications|limit exceeded)/

function isResizeObserverLoopError (message: unknown): boolean {
  return typeof message === 'string' && resizeObserverLoopError.test(message)
}

function suppressResizeObserverLoopError (event: ErrorEvent): void {
  const message = event.message || (event.error instanceof Error ? event.error.message : '')

  if (!isResizeObserverLoopError(message)) {
    return
  }

  event.stopImmediatePropagation()
  event.preventDefault()
}

window.addEventListener('error', suppressResizeObserverLoopError, true)

const previousWindowOnError = window.onerror

window.onerror = (message, source, lineno, colno, error) => {
  if (isResizeObserverLoopError(message) || isResizeObserverLoopError(error?.message)) {
    return true
  }

  if (previousWindowOnError) {
    return previousWindowOnError.call(window, message, source, lineno, colno, error)
  }

  return false
}

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement)

root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
)

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals()
